import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { signInWithPopup } from "firebase/auth";
import { loginUserThunk, firebaseLoginThunk } from "../../store/slices/authThunks";
import { logout } from "../../store/slices/authSlice";
import { auth, googleProvider, githubProvider } from "../../firebaseConfig";
import loginIllustration from "../../images/Learning language with friend.png";
import logo from "../../images/Nkenne Ai-01 2.png";
import { EyeOff } from "lucide-react";
import { getStaticUrl } from "../../config/domains";
const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "user"
  });
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, isAuthenticated, role } = useSelector((state) => state.auth);

  // Handle redirect from URL query params (cross-domain redirects)
  useEffect(() => {
    if (isAuthenticated) {
      // Check URL params first (for cross-domain redirects from static site)
      const urlParams = new URLSearchParams(location.search);
      const redirectParam = urlParams.get('redirect');
      const planParam = urlParams.get('plan');

      if (redirectParam) {
        // Parse plan from URL if exists
        let plan = null;
        if (planParam) {
          try {
            plan = JSON.parse(decodeURIComponent(planParam));
          } catch (e) {
            console.error('Error parsing plan from URL:', e);
          }
        }

        navigate(redirectParam, {
          state: plan ? { plan } : {},
          replace: true
        });
        return;
      }

      // Check if there's a redirect path from location state
      const redirectTo = location.state?.redirectTo;

      if (redirectTo) {
        navigate(redirectTo, {
          state: {
            selectedPlan: location.state?.selectedPlan
          }
        });
      } else {
        // Only redirect non-admin users (admin users are already handled in login handlers)
        if (role !== 'admin') {
          navigate('/user-dashboard');
        }
      }
    }
  }, [isAuthenticated, role, navigate, location.state, location.search]);

  // Clear error when component mounts
  useEffect(() => {
    // dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      return;
    }

    try {
      const result = await dispatch(loginUserThunk(formData));
      if (loginUserThunk.fulfilled.match(result)) {
        // Check if user is admin - block admin login
        if (result.payload.role === 'admin') {
          toast.error("Admin users cannot login through this portal. Please use the admin portal.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          // Logout the user immediately if they were authenticated
          dispatch(logout());
          return;
        }

        toast.success("Login successful! Welcome back!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        // After successful login, go to dashboard
        navigate("/user-dashboard");
      } else if (loginUserThunk.rejected.match(result)) {
        // Check if error is due to unverified email
        if (result.payload?.requiresVerification || result.payload?.includes('verify your email')) {
          toast.warning("Please verify your email before logging in. Redirecting to verification page...", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });

          // Redirect to verification page
          setTimeout(() => {
            navigate("/verify-email", {
              state: { email: formData.email }
            });
          }, 1500);
        } else {
          toast.error(result.payload || "Login failed. Please check your credentials.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error("An unexpected error occurred. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Handle social login (Google & GitHub)
  const handleSocialLogin = async (provider, providerName) => {
    try {
      // Step 1: Sign in with Firebase
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Step 2: Get Firebase ID token
      const idToken = await user.getIdToken();

      // Step 3: Send token to backend for verification
      const loginResult = await dispatch(firebaseLoginThunk(idToken));

      if (firebaseLoginThunk.fulfilled.match(loginResult)) {
        // Check if user is admin - block admin login
        if (loginResult.payload.role === 'admin') {
          toast.error("Admin users cannot login through this portal. Please use the admin portal.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          // Logout the user immediately if they were authenticated
          dispatch(logout());
          return;
        }

        const { isNewUser } = loginResult.payload;

        toast.success(
          isNewUser
            ? `Welcome to NKENNEAi! Account created successfully with ${providerName}.`
            : `Welcome back! Logged in successfully with ${providerName}.`,
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );

        // Check URL params first (for cross-domain redirects)
        const urlParams = new URLSearchParams(location.search);
        const redirectParam = urlParams.get('redirect');

        if (redirectParam) {
          navigate(redirectParam, { replace: true });
        } else {
          // Check if there's a redirect path from location state
          const redirectTo = location.state?.redirectTo;

          if (redirectTo) {
            navigate(redirectTo, {
              state: {
                selectedPlan: location.state?.selectedPlan
              }
            });
          } else {
            // After successful social login, go to dashboard
            // Only show welcome modal and onboarding for new users
            if (isNewUser) {
              navigate("/user-dashboard", {
                state: { showWelcomeModal: true, showOnboarding: true }
              });
            } else {
              navigate("/user-dashboard");
            }
          }
        }
      } else if (firebaseLoginThunk.rejected.match(loginResult)) {
        toast.error(loginResult.payload || `Failed to login with ${providerName}. Please try again.`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error(`${providerName} login error:`, error);

      // Handle specific Firebase errors
      let errorMessage = `Failed to login with ${providerName}. Please try again.`;

      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account with this email already exists. Please use your original sign-in method.';
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error. Please check your connection.';
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };



  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Header with Logo and Back Button */}
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-4 md:p-6">
        {/* Logo */}
        <div onClick={() => window.open(getStaticUrl('/'), '_blank', 'noopener,noreferrer')} className="flex items-center bg-white rounded-lg px-3 py-2 shadow-sm cursor-pointer">
          <img src={logo} alt="NkenneAi Logo" className="w-full h-8  md:h-10" />
        </div>
        {/* Back Button */}
        <button
          onClick={() => window.open(getStaticUrl('/'), '_blank', 'noopener,noreferrer')}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Website
        </button>
      </div>

      {/* Left Side - Illustration */}
      <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
        <img
          src={loginIllustration}
          alt="Illustration"
          className="max-w-[70%] h-auto"
        />
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8 pt-20 md:pt-24">
        <div className="w-full max-w-md space-y-6">
          {/* Logo and Branding */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src={logo} alt="Logo" className="w-32 md:w-50 md:h-50" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
              Sign in to NKENNEAi
            </h1>
          </div>

          <div className="space-y-6 border p-6 rounded-xl">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Enter your email"
                  required
                />
              </div>

              {/* Password Input */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label
                    htmlFor="password"
                    className="block text-sm font-bold text-gray-700"
                  >
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-secondary hover:text-teal-500 font-bold"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" color="gray" />
                    ) : (
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-secondary hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500 font-bold">OR</span>
              </div>
            </div>

            {/* Demo Login Buttons */}
            <div className="space-y-3">
              <p className="text-center text-sm text-gray-600 mb-3">
                Try the demo with different roles:
              </p>
            </div>

            {/* Social Login Buttons */}
            <div className="space-y-3">
              {/* Google Button */}
              <button
                onClick={() => handleSocialLogin(googleProvider, 'Google')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm font-bold"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* GitHub Button */}
              <button
                onClick={() => handleSocialLogin(githubProvider, 'GitHub')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm font-bold"
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                Continue with Github
              </button>
            </div>

            {/* Sign Up Link */}
            <p className="text-center text-sm text-gray-600 font-bold">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="text-secondary hover:text-teal-500 font-bold"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
